import { StrictMode } from 'react';
import { render } from 'react-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { Router } from 'react-router-dom';
import { FlagsProvider } from 'react-unleash-flags';
import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

import App from 'containers/App';
import LanguageProvider from 'containers/LanguageProvider';
import GlobalAgreementModal from 'containers/AgreementModalProvider';
import StripeProvider from 'containers/Stripe';
import NetworkStatus from 'components/NetworkStatus';
import GlobalError from 'componentsV2/GlobalError';
import { SENTRY_EXPORT_VIEW_NAME } from 'components/Modals/ExportView';
import FlashMessageProvider from 'containers/FlashMessageProvider';
import IntercomProvider from 'containers/IntercomProvider';

import { AbilityProvider } from 'permissions';

import store, { history } from './store';
import { translationMessages } from './i18n';
import {
  FLAG_CONFIG,
  SENTRY_DSN,
  SENTRY_SAMPLE_RATE,
  SENTRY_TRACING_ORIGINS,
  MIXPANEL_API_KEY,
  MIXPANEL_DEBUG,
  MIXPANEL_TEST,
  SENTRY_ENV,
} from './env';

// Global styles
import 'sanitize.css/sanitize.css';
import 'styles/theme/src/semantic.less';

const renderApp = (messages: { en: object; fr: object }) => {
  const appNode = document.getElementById('app');

  if (appNode) {
    render(
      <StrictMode>
        <Sentry.ErrorBoundary fallback={<GlobalError />}>
          <ReduxProvider store={store}>
            <AbilityProvider>
              <FlagsProvider config={FLAG_CONFIG}>
                <LanguageProvider messages={messages}>
                  <FlashMessageProvider>
                    <Router history={history}>
                      <NetworkStatus />
                      <GlobalAgreementModal>
                        <StripeProvider>
                          <IntercomProvider>
                            <App />
                          </IntercomProvider>
                        </StripeProvider>
                      </GlobalAgreementModal>
                    </Router>
                  </FlashMessageProvider>
                </LanguageProvider>
              </FlagsProvider>
            </AbilityProvider>
          </ReduxProvider>
        </Sentry.ErrorBoundary>
      </StrictMode>,
      appNode
    );
  }
};

if (typeof __DEV__ !== 'undefined' && !__DEV__) {
  const stagingTag = __STAGING__ ? '_STAGING_' : '';

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    integrations: [Sentry.browserTracingIntegration(), Sentry.reactRouterV5BrowserTracingIntegration({ history })],
    normalizeDepth: 6, // redux state depth
    tracesSampler: (samplingContext) => {
      if (samplingContext.name === SENTRY_EXPORT_VIEW_NAME) {
        // Trace all exports, to see if they have an impact on the back-end.
        // Part of an on-going investigation to improve DB over-utilisation.
        return 1;
      }
      return SENTRY_SAMPLE_RATE;
    },
    tracePropagationTargets: SENTRY_TRACING_ORIGINS,
    release: `FLEET_FE_${stagingTag}${__VERSION__}`,
    denyUrls: [/web\.delighted\.com/i, /maps\.googleapis\.com/i],
  });
}

if (typeof __TESTING__ === 'undefined' || __TESTING__) {
  mixpanel.init(MIXPANEL_API_KEY, { debug: MIXPANEL_DEBUG, test: MIXPANEL_TEST });
}

renderApp(translationMessages);

export default store;
